import { Autocomplete, FormControl, FormLabel, TextField } from '@mui/material';
import { useEffect, useMemo } from 'react';

import { useEnvironment } from '@/contexts/useEnvironment';

import { EmptySelect } from './emptySelect';

import type { AzureDiskConfigState } from '../restore/restoreAzureVMWizard';

const title = 'Virtual machine size';

/**
 * Azure Disk Storage Account Types that require PremiumIO capability on the VM.
 * These values correspond to Azure SDK's DiskStorageAccountTypes enum.
 * See: shared/azure/vm/azure_vm.go for backend usage
 */
enum AzurePremiumStorageType {
  PremiumLRS = 'Premium_LRS',
  PremiumZRS = 'Premium_ZRS',
  PremiumV2LRS = 'PremiumV2_LRS',
  UltraSSDLRS = 'UltraSSD_LRS',
}

export const PREMIUM_STORAGE_TYPES = Object.values(AzurePremiumStorageType);

interface AzureVmSku {
  name?: string;
  capabilities?: Record<string, string>;
}

export const VirtualMachineSizeSelect = ({
  skus,
  vmSize,
  onChange,
  disksState,
  originalVMSize,
  autoSuggest,
}: {
  skus?: AzureVmSku[];
  vmSize?: string;
  disksState: AzureDiskConfigState[] | undefined;
  onChange: (vmSize: string) => void;
  originalVMSize: string | undefined;
  autoSuggest?: boolean;
}) => {
  const { isDemo } = useEnvironment();
  const bodySkus = skus ?? [];

  // Filter compatible SKUs and sort by distance from the original VM size.
  // Closest matches (similar vCPUs/memory) appear first in the dropdown.
  const SKUChoices = useMemo(() => {
    const dataDisksLength = (disksState?.length ?? 1) - 1;
    const originalVMSKU = bodySkus.find((x) => x.name === originalVMSize);
    const diskHyperVGenerations = Array.from(
      new Set(disksState?.map((d) => d.diskSettings.settings.hyperVGeneration))
    ).filter(Boolean);

    // Check if any disk uses premium storage (type contains "Premium" or is UltraSSD)
    // The restore flow defaults to Premium_LRS, so if no type is set, assume premium
    const requiresPremiumStorage =
      !disksState ||
      disksState.length === 0 ||
      disksState.some((d) => {
        const diskType = d.diskSettings.settings.type;
        // If disk type is not set, the backend defaults to Premium_LRS
        if (!diskType) return true;
        return PREMIUM_STORAGE_TYPES.some((premiumType) =>
          diskType.includes(premiumType)
        );
      });

    // Use vCPUsAvailable (actual usable cores) over vCPUs (base tier cores)
    // to correctly handle constrained-core variants (e.g. Standard_E8-2ds_v4
    // reports vCPUs=8 but vCPUsAvailable=2).
    const origCpuArchitectureType =
      originalVMSKU?.capabilities?.CpuArchitectureType ?? '';
    const origVCPUs = Number(
      originalVMSKU?.capabilities?.vCPUsAvailable ??
        originalVMSKU?.capabilities?.vCPUs ??
        0
    );
    const origMemGB = Number(originalVMSKU?.capabilities?.MemoryGB ?? 0);
    const hasOriginalSpecs = origVCPUs > 0 || origMemGB > 0;

    return bodySkus
      .filter((sku) => {
        if (isDemo) {
          return true;
        }
        const skuGenerations = sku.capabilities?.HyperVGenerations?.split(',');

        // Filter out VM sizes that don't support Premium storage when required
        // Note: PremiumIO is returned as string 'True'/'False' from Azure API, not boolean
        const supportsPremiumStorage =
          sku.capabilities?.PremiumIO?.toLowerCase() === 'true';
        if (requiresPremiumStorage && !supportsPremiumStorage) {
          return false;
        }

        return (
          (origCpuArchitectureType === '' ||
            (sku.capabilities?.CpuArchitectureType ?? '') ===
              origCpuArchitectureType) &&
          Number(sku.capabilities?.MaxDataDiskCount ?? '') >= dataDisksLength &&
          diskHyperVGenerations.every(
            (g) =>
              typeof g === 'string' &&
              Array.isArray(skuGenerations) &&
              skuGenerations.includes(g)
          )
        );
      })
      .sort((a, b) => {
        // If original VM specs are unknown (SKU not in region), sort alphabetically
        if (!hasOriginalSpecs) {
          return (a.name ?? '').localeCompare(b.name ?? '');
        }
        // Sort by similarity to original VM: penalize downgrades 3x,
        // normalize memory by /4 so 1 vCPU ≈ 4 GB in scoring weight.
        const scoreOf = (sku: typeof a) => {
          const availVCPUs = Number(
            sku.capabilities?.vCPUsAvailable ?? sku.capabilities?.vCPUs ?? 0
          );
          const memGB = Number(sku.capabilities?.MemoryGB ?? 0);
          const vcpuDiff = availVCPUs - origVCPUs;
          const memDiff = memGB - origMemGB;
          return (
            (vcpuDiff < 0 ? Math.abs(vcpuDiff) * 3 : vcpuDiff) +
            (memDiff < 0 ? Math.abs(memDiff) * 3 : memDiff) / 4
          );
        };
        return scoreOf(a) - scoreOf(b);
      })
      .map((sku) => sku.name)
      .filter((name): name is string => Boolean(name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disksState, bodySkus, originalVMSize]);

  // Keep selected size aligned with current available options.
  // In autoSuggest mode, always choose the best option when current one is invalid.
  // In manual mode, clear stale invalid selections.
  useEffect(() => {
    const currentVmSize = vmSize ?? '';

    if (SKUChoices.length === 0) {
      if (currentVmSize !== '') {
        onChange('');
      }
      return;
    }

    const hasValidSelection =
      currentVmSize !== '' && SKUChoices.includes(currentVmSize);

    if (autoSuggest) {
      if (!hasValidSelection) {
        onChange(SKUChoices[0]);
      }
      return;
    }

    if (currentVmSize !== '' && !hasValidSelection) {
      onChange('');
    }
  }, [autoSuggest, SKUChoices, vmSize, onChange]);

  if (!skus?.length) {
    return <EmptySelect title={title} />;
  }

  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>{title}</FormLabel>
      <Autocomplete
        clearOnBlur
        size='small'
        value={vmSize || null}
        onChange={(event, newValue) => onChange(newValue || '')}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              sx={{
                '& .MuiInputBase-input': {
                  height: '40px',
                },
              }}
              placeholder='Select'
              label=''
              InputLabelProps={{ shrink: true }}
            />
          );
        }}
        options={SKUChoices}
      />
    </FormControl>
  );
};
